import React, { useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowUpRight, FiRotateCcw, FiSearch, FiSliders, FiX } from "react-icons/fi";
import { servicesData } from "../Components/serviceMetaData";
import { StoriesData } from "../Components/storiesMetaData";
import { getPublicSeoRoutes, serviceSlug } from "../seo/publicSeo";
import { voicesSolutionsData } from './voicesSolutionsData';
import CustomerVoices from './CustomerVoices';
import VoicesCustomerMarks from './VoicesCustomerMarks';
import { useMotionPreference } from './useScrollScene';
import "./voices-directory.css";
import VoicesServices from './VoicesServices';

const pageCopy = {
  work: { label: "Customer stories", title: "Our work", lead: 'The work behind', accent: 'the words.', description: 'From the hotelier finding an answer to the team making sense of recurring revenue. The human side of connected systems.', unit: "stories", filter: "Industry" },
  "site-map": { label: "Site directory", title: "Site map", lead: 'Everything.', accent: 'One clear place.', description: "Explore Antrazal's services, customer stories, company information, and practical resources.", unit: "pages", filter: "Page type" },
};

const pageNames = {
  "/": "Home",
  "/who-we-are": "Who we are",
  "/careers": "Careers",
  "/products/edi-converter": "EDI Converter",
  "/events": "Events",
  "/case-studies": "Case studies & white papers",
  "/privacy-policy": "Privacy policy",
  "/services": "Services",
  "/work": "Our work",
  "/contact": "Contact",
  "/site-map": "Site map",
  "/career": "Careers (legacy URL)",
  "/edi-converter": "EDI Converter (legacy URL)",
};

function serviceCategory(service) {
  // Fill metadata gaps and keep Azure in the existing navigation's DevOps group.
  if (service.name === "Azure") return "DevOps";
  if (service.name === "HubSpot Implementation") return "Marketing Automation";
  const category = service.category?.trim();
  if (category) return /^others?$/i.test(category) ? "Others" : category;
  return service.name.startsWith("Salesforce") ? "Salesforce" : "Others";
}

function headlineText(value = "") {
  if (!/[<&]/.test(value)) return value;
  // Some current headlines contain formatting. Read their text without mounting HTML.
  const document = new DOMParser().parseFromString(value, "text/html");
  document.querySelectorAll("script, style").forEach((node) => node.remove());
  document.querySelectorAll("br").forEach((node) => node.replaceWith(" "));
  return document.body.textContent.replace(/\s+/g, " ").trim();
}

function serviceEntries() {
  const agentEntries = Object.values(voicesSolutionsData).filter(page => ['agents', 'managed'].includes(page.type)).map(page => ({ path: page.path, title: page.name, description: page.description, category: 'AI' }));
  return [...agentEntries, ...servicesData.filter((service) => service.name?.trim()).map((service) => ({
    path: `/services/${serviceSlug(service.name)}`,
    title: service.name,
    description: service.shortDescription || service.headline || "",
    category: serviceCategory(service),
  })), ...Object.values(voicesSolutionsData).filter(page => page.type === 'service').map(page => ({ path: page.path, title: page.name, description: page.description, category: page.category }))];
}

function storyEntries() {
  const featured = ['trivago-support-portal', '24-hour-home-care-workforce-modernization', 'making-arr-easier-to-explain'];
  return StoriesData.filter((story) => story.url?.trim()).slice().sort((a, b) => {
    const rank = (story) => featured.includes(story.url) ? featured.indexOf(story.url) : featured.length;
    return rank(a) - rank(b);
  }).map((story) => ({
    path: `/customer-stories/${story.url}`,
    title: headlineText(story.headline || story.detailPageHeadline),
    description: story.description || "",
    customer: story.customer,
    image: story.backgroundImage,
    category: story.industry || "Other industries",
  }));
}

function directoryEntries(kind, blogs) {
  if (kind === "work") return storyEntries();

  const details = new Map([...serviceEntries(), ...storyEntries()].map((entry) => [entry.path, entry]));
  const entries = getPublicSeoRoutes(blogs).map((route) => {
    const detail = details.get(route.pathname);
    let category = "Explore";
    if (route.pathname.startsWith("/services/")) category = "Services";
    if (route.pathname.startsWith("/customer-stories/")) category = "Customer stories";
    if (route.pathname.startsWith("/article/") || route.pathname.startsWith("/blog/")) category = "Articles";
    return {
      path: route.pathname,
      title: detail?.title || pageNames[route.pathname] || route.title.replace(/ \| Antrazal$/, ""),
      description: route.description,
      customer: detail?.customer,
      category,
    };
  });

  // New routes are integrated by the app. Include existing redirects without private namespaces.
  ["/services", "/work", "/contact", "/site-map", "/career", "/edi-converter"].forEach((path) => {
    if (!entries.some((entry) => entry.path === path)) {
      entries.push({ path, title: pageNames[path], category: path === "/career" || path === "/edi-converter" ? "Legacy URLs" : "Explore" });
    }
  });
  return Array.from(new Map(entries.map((entry) => [entry.path, entry])).values());
}

function Directory({ kind, blogs }) {
  const copy = pageCopy[kind];
  const id = useId();
  const searchRef = useRef(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const entries = useMemo(() => directoryEntries(kind, blogs), [kind, blogs]);
  const categories = Array.from(new Set(entries.map((entry) => entry.category)));
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matchesSearch = (entry) => {
    const text = [entry.title, entry.description, entry.customer, entry.category].filter(Boolean).join(" ").toLowerCase();
    return terms.every((term) => text.includes(term));
  };
  const searched = entries.filter(matchesSearch);
  const results = searched.filter((entry) => !category || entry.category === category);
  const groups = categories.map((name) => ({ name, entries: results.filter((entry) => entry.category === name) }));

  function reset() {
    setQuery("");
    setCategory("");
    searchRef.current?.focus();
  }

  function entryLink(entry) {
    return (
      <li key={entry.path} className="v-directory-item">
        <Link to={entry.path} className="v-directory-link" aria-label={entry.title}>
          <div className="v-directory-item-copy">
            <h3 className="v-directory-item-title">{entry.title}</h3>
            {kind === "site-map" && entry.customer && <p className="v-directory-customer">{entry.customer}</p>}
          </div>
          <FiArrowUpRight className="v-directory-arrow" aria-hidden="true" />
        </Link>
      </li>
    );
  }

  return (
    <main className={`voices-site v-directory v-directory--reference v-directory--${kind}`} data-voices-theme="dark" aria-labelledby={`${id}-title`}>
      <div className="v-directory-inner">
        <header className="v-directory-intro">
          <nav className="v-directory-breadcrumb" aria-label="Breadcrumb"><Link to="/">Home</Link><span aria-hidden="true">/</span><span aria-current="page">{copy.title}</span></nav>
          <p className="v-directory-label">{copy.label}</p>
          <h1 id={`${id}-title`} className="v-directory-title">{copy.lead}{' '}<br /><em>{copy.accent}</em></h1>
          <p className="v-directory-intro-description">{copy.description}</p>
        </header>

        <details className="v-directory-filter-panel">
        <summary className="v-directory-filter-toggle" aria-label={`Filter ${copy.unit}`} title={`Filter ${copy.unit}`}><FiSliders aria-hidden="true" /></summary>
        <form className="v-directory-controls" role="search" aria-label={`${copy.title} directory`} onSubmit={(event) => event.preventDefault()}>
          <label className="v-directory-field" htmlFor={`${id}-category`}>
            <span>{copy.filter}</span>
            <select id={`${id}-category`} aria-label={copy.filter} value={category} onChange={(event) => setCategory(event.target.value)} aria-controls={`${id}-results`}>
              <option value="">All {copy.unit} ({searched.length})</option>
              {categories.map((name) => (
                <option key={name} value={name}>{name} ({searched.filter((entry) => entry.category === name).length})</option>
              ))}
            </select>
          </label>
          <div className="v-directory-field v-directory-search-field">
            <label htmlFor={`${id}-search`}>Search {copy.unit}</label>
            <div className="v-directory-search">
              <FiSearch aria-hidden="true" />
              <input id={`${id}-search`} ref={searchRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)} aria-controls={`${id}-results`} />
              <button className="v-directory-icon-button" type="button" aria-label="Clear search" title="Clear search" disabled={!query} onClick={() => { setQuery(""); searchRef.current?.focus(); }}>
                <FiX aria-hidden="true" />
              </button>
            </div>
          </div>
        </form>

        <div className="v-directory-summary">
              <p className="v-directory-count" role="status" aria-live="polite" aria-atomic="true">{results.length} of {entries.length} {copy.unit}</p>
          {(query || category) && <button className="v-directory-reset" type="button" onClick={reset}><FiRotateCcw aria-hidden="true" /> Reset filters</button>}
        </div>
        </details>

        <div id={`${id}-results`} className="v-directory-results">
          {results.length === 0 ? (
            <section className="v-directory-empty" aria-labelledby={`${id}-empty`}>
              <FiSearch aria-hidden="true" />
              <h2 id={`${id}-empty`}>No {copy.unit} found</h2>
              <p>No matches{query.trim() && <> for <strong>&quot;{query.trim()}&quot;</strong></>}{category && <> in <strong>{category}</strong></>}.</p>
              <button type="button" className="v-directory-reset" onClick={reset}><FiRotateCcw aria-hidden="true" /> Show all {copy.unit}</button>
            </section>
          ) : kind === "work" ? (
            <ul className="v-directory-story-grid" aria-label="Customer stories">
              {results.map((entry) => (
                <li key={entry.path} className="v-directory-story">
                  <Link to={entry.path} className="v-directory-story-link" aria-label={`${entry.customer}: ${entry.title}`}>
                    <div className="v-directory-photo-wrap"><img className="v-directory-photo" src={entry.image} alt={entry.customer} width="1200" height="800" loading="lazy" decoding="async" /><FiArrowUpRight className="v-directory-photo-arrow" aria-hidden="true" /></div>
                    <div className="v-directory-story-copy">
                      <p className="v-directory-customer">{entry.customer}</p>
                      <h2 className="v-directory-item-title">{entry.title}</h2>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="v-directory-groups">
              {groups.filter((group) => group.entries.length).map((group) => (
                <section key={group.name} className="v-directory-group" aria-label={group.name}>
                  <div className="v-directory-group-heading">
                    <h2>{group.name}</h2>
                    <span aria-label={`${group.entries.length} ${copy.unit}`}>{group.entries.length}</span>
                  </div>
                  <ul className="v-directory-list">{group.entries.map((entry) => entryLink(entry))}</ul>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
      {kind === 'work' && <WorkRelationships />}
    </main>
  );
}

function WorkRelationships() {
  const [reduced] = useMotionPreference();
  return <div className="v-directory-relationships"><VoicesCustomerMarks /><CustomerVoices reducedMotion={reduced} /></div>;
}

export default function VoicesDirectory({ kind = "services", blogs = [] }) {
  if (kind === 'services') return <VoicesServices />;
  return <Directory key={kind} kind={kind} blogs={blogs} />;
}
